import React, { useCallback, useEffect, useRef, useState } from 'react';
import './styles.scss';

// The teaching menu (client: profMenuService.ts, opened with F3 by default). A teacher, or staff, picks a spell
// and gives it to the person they aimed at when they opened the menu, or takes it back. The server rules on
// who may teach, how far the student is and what the spell is (systems/spellSystem.ts); this only asks and
// shows the answers. The student is whoever was under the crosshair, shown by the name the teacher is
// allowed to know (a stranger stays anonymous), or the teacher themself.

interface StudentView {
  ok: boolean;
  self: boolean;
  label: string;
  reason: string;
}

interface Spell {
  id: number;
  n: string;
  edid: string;
  s: string;
  mod: string;
  k: boolean;
}

interface Facet {
  id: string;
  label: string;
  count: number;
}

interface ResultsDetail {
  ready: boolean;
  query: string;
  school: string;
  offset: number;
  total: number;
  schools?: Facet[];
  results?: Spell[];
  student: StudentView;
}

interface TaughtDetail {
  ok: boolean;
  spellId: number;
  remove: boolean;
  message: string;
}

const SCHOOL_LABELS: Record<string, string> = {
  res: 'Restauration',
  des: 'Destruction',
  alt: 'Altération',
  con: 'Conjuration',
  ill: 'Illusion',
  oth: 'Autres',
};

const PAGE = 60;
const SEARCH_DEBOUNCE_MS = 220;
const NOT_READY_RETRY_MS = 2000;

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ source: 'prof-menu', ...payload }));
  } catch (e) {
    // running outside the game (e.g. Storybook)
  }
};

const ProfMenu: React.FC = () => {
  const [shown, setShown] = useState(false);
  const [student, setStudent] = useState<StudentView>({ ok: true, self: true, label: 'Toi-même', reason: '' });
  const [useSelf, setUseSelf] = useState(false);
  const [query, setQuery] = useState('');
  const [school, setSchool] = useState('all');
  const [schools, setSchools] = useState<Facet[]>([]);
  const [spells, setSpells] = useState<Spell[]>([]);
  const [total, setTotal] = useState(0);
  const [ready, setReady] = useState(true);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const retry = useRef<number | undefined>(undefined);
  // What the last request asked for: an answer for anything else is dropped
  const asked = useRef({ query: '', school: 'all', offset: 0, self: false });
  const selfRef = useRef(false);
  selfRef.current = useSelf;

  const search = useCallback((q: string, sc: string, offset: number) => {
    asked.current = { query: q, school: sc, offset, self: selfRef.current };
    setLoading(true);
    send({ action: 'search', query: q, school: sc, offset, limit: PAGE, self: selfRef.current });
  }, []);

  useEffect(() => {
    const onShow = (e: Event) => {
      const d = ((e as CustomEvent).detail ?? {}) as { student?: StudentView };
      setShown(true);
      setUseSelf(false);
      selfRef.current = false;
      setQuery('');
      setSchool('all');
      setSpells([]);
      setTotal(0);
      setStatus(null);
      if (d.student) setStudent(d.student);
      search('', 'all', 0);
    };
    const onHide = () => {
      setShown(false);
      window.clearTimeout(timer.current);
      window.clearTimeout(retry.current);
    };
    const onResults = (e: Event) => {
      const d = (e as CustomEvent).detail as ResultsDetail;
      const want = asked.current;
      if (d.query !== want.query || d.school !== want.school) return;
      setLoading(false);
      if (d.student) setStudent(d.student);
      if (!d.ready) {
        setReady(false);
        window.clearTimeout(retry.current);
        retry.current = window.setTimeout(() => search(want.query, want.school, want.offset), NOT_READY_RETRY_MS);
        return;
      }
      setReady(true);
      setTotal(d.total);
      if (d.schools) setSchools(d.schools);
      const rows = d.results ?? [];
      setSpells((prev) => (d.offset === 0 ? rows : [...prev, ...rows]));
    };
    const onTaught = (e: Event) => {
      const d = (e as CustomEvent).detail as TaughtDetail;
      setStatus({ ok: d.ok, text: d.message });
      if (d.ok) setSpells((prev) => prev.map((s) => (s.id === d.spellId ? { ...s, k: !d.remove } : s)));
    };
    window.addEventListener('skymp5-client:showProfMenu', onShow);
    window.addEventListener('skymp5-client:hideProfMenu', onHide);
    window.addEventListener('skymp5-client:profResults', onResults);
    window.addEventListener('skymp5-client:profTaught', onTaught);
    return () => {
      window.removeEventListener('skymp5-client:showProfMenu', onShow);
      window.removeEventListener('skymp5-client:hideProfMenu', onHide);
      window.removeEventListener('skymp5-client:profResults', onResults);
      window.removeEventListener('skymp5-client:profTaught', onTaught);
      window.clearTimeout(timer.current);
      window.clearTimeout(retry.current);
    };
  }, [search]);

  // Escape closes the menu (the browser has the focus while it is open)
  useEffect(() => {
    if (!shown) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') send({ action: 'close' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shown]);

  if (!shown) return null;

  const onQuery = (value: string) => {
    setQuery(value);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => search(value, school, 0), SEARCH_DEBOUNCE_MS);
  };

  const onSchool = (id: string) => {
    setSchool(id);
    search(query, id, 0);
  };

  const onSelf = (value: boolean) => {
    setUseSelf(value);
    selfRef.current = value;
    setStatus(null);
    search(query, school, 0);
  };

  const teach = (spell: Spell) => {
    setStatus(null);
    send({ action: 'teach', spellId: spell.id, remove: spell.k, self: useSelf });
  };

  const usable = student.ok;
  const aimed = !student.self;

  return (
    <div className="prof-menu" role="dialog" aria-label="Enseignement">
      <div className="prof-menu__head">
        <div>
          <h2 className="prof-menu__title">Enseignement</h2>
          <p className="prof-menu__sub">Choisis un sort à transmettre à ton élève.</p>
        </div>
        <button type="button" className="prof-menu__close" onClick={() => send({ action: 'close' })}>
          Fermer
        </button>
      </div>

      <div className={'prof-menu__student' + (usable ? '' : ' is-blocked')}>
        <div className="prof-menu__student-text">
          <span className="prof-menu__student-label">Élève</span>
          <span className="prof-menu__student-name">{usable ? student.label : 'Personne'}</span>
          {!usable ? <span className="prof-menu__student-reason">{student.reason || 'Aucun élève.'}</span> : null}
        </div>
        <div className="prof-menu__student-actions">
          {aimed || useSelf ? (
            <button type="button" className="prof-menu__link" onClick={() => onSelf(!useSelf)}>
              {useSelf ? 'Revenir à la personne visée' : 'Me choisir moi-même'}
            </button>
          ) : null}
        </div>
      </div>

      <div className="prof-menu__tools">
        <input
          type="text"
          className="prof-menu__search"
          placeholder="Chercher un sort"
          value={query}
          maxLength={60}
          onChange={(e) => onQuery(e.target.value)}
          autoFocus
        />
        <div className="prof-menu__schools" role="tablist" aria-label="École de magie">
          <button
            type="button"
            role="tab"
            aria-selected={school === 'all'}
            className={'prof-menu__school' + (school === 'all' ? ' is-active' : '')}
            onClick={() => onSchool('all')}
          >
            Toutes
          </button>
          {schools.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={school === s.id}
              className={'prof-menu__school' + (school === s.id ? ' is-active' : '')}
              onClick={() => onSchool(s.id)}
            >
              {s.label}
              <span className="prof-menu__count">{s.count}</span>
            </button>
          ))}
        </div>
      </div>

      {status ? (
        <div className={'prof-menu__status' + (status.ok ? ' is-ok' : ' is-error')} role="status">
          {status.text}
        </div>
      ) : null}

      <div className="prof-menu__list">
        {!ready ? (
          <p className="prof-menu__empty">Le catalogue des sorts se prépare, quelques secondes.</p>
        ) : spells.length === 0 && !loading ? (
          <p className="prof-menu__empty">Aucun sort ne correspond.</p>
        ) : (
          spells.map((s) => (
            <div className="prof-menu__row" key={s.id}>
              <div className="prof-menu__row-text">
                <span className="prof-menu__row-name">{s.n}</span>
                <span className="prof-menu__row-meta">{SCHOOL_LABELS[s.s] ?? s.s}</span>
              </div>
              {s.k ? <span className="prof-menu__known">Connu</span> : null}
              <button
                type="button"
                className={'prof-menu__action' + (s.k ? ' is-remove' : ' is-teach')}
                disabled={!usable}
                onClick={() => teach(s)}
              >
                {s.k ? 'Retirer' : 'Enseigner'}
              </button>
            </div>
          ))
        )}
        {ready && spells.length < total ? (
          <button
            type="button"
            className="prof-menu__more"
            disabled={loading}
            onClick={() => search(query, school, spells.length)}
          >
            Afficher la suite ({total - spells.length})
          </button>
        ) : null}
      </div>

      <div className="prof-menu__foot">
        Pour changer d'élève, ferme le menu, vise la personne et rouvre-le avec la même touche.
      </div>
    </div>
  );
};

export default ProfMenu;
